/*
 * ข้อมูลบริษัท / แบรนด์ ของเอกสาร (C2, choices 28 ต.ค. 2569: หลายบริษัท/แบรนด์ เลือกตอนกรอก · ผู้ดูแล + คนที่มีสิทธิ์
 * "เทมเพลต" แก้ได้ · ถ้าไม่มีค่ากลาง ให้กรอกเอง). D1 doc_profiles (0029), worker/index.ts docProfileRoutes.
 * Plain data: the same checker for the app (before sending) and the server (BUG-13).
 */

export type DocLang = 'th' | 'en';
export const DOC_LANGS: readonly DocLang[] = ['th', 'en'];
export const DOC_LANG_TH: Record<DocLang, string> = { th: 'ไทย', en: 'English' };

/** what a profile can fill: a name and an address in each language; the rest is the same in both */
export const PROFILE_KEYS = ['name', 'address', 'phone', 'email', 'taxId', 'website'] as const;
export type ProfileKey = typeof PROFILE_KEYS[number];
export const PROFILE_KEY_TH: Record<ProfileKey, string> = {
  name: 'ชื่อบริษัท/แบรนด์', address: 'ที่อยู่', phone: 'โทรศัพท์', email: 'อีเมล', taxId: 'เลขประจำตัวผู้เสียภาษี', website: 'เว็บไซต์'
};
export interface DocProfileData {
  th: { name: string; address: string };
  en: { name: string; address: string };
  phone: string; email: string; taxId: string; website: string;
}
export interface DocProfileInput { label: string; data: DocProfileData; logo: string | null }

const LIMIT: Record<ProfileKey, number> = { name: 160, address: 400, phone: 60, email: 120, taxId: 30, website: 200 };
export const PROFILE_LABEL_MAX = 60;
/** a logo: one of the uploader's pictures (POST /api/assets → /api/assets/img/<sha-256>.<ext>) */
export const LOGO_URL = /^\/api\/assets\/(img\/[0-9a-f]{64}\.(?:png|jpg|webp))$/;

type Checked<T> = { ok: true; value: T } | { ok: false; error: string };
const str = (v: unknown) => typeof v === 'string' ? v.replace(/\r\n?/g, '\n').trim() : '';
const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** the label, data and logo of a profile, or why not (Thai) */
export function checkDocProfile(raw: unknown): Checked<DocProfileInput> {
  if (!isObj(raw)) return { ok: false, error: 'ข้อมูลบริษัทที่ส่งมาไม่ใช่รูปแบบที่ระบบรับ' };
  const label = str(raw.label);
  if (!label) return { ok: false, error: 'ตั้งชื่อเรียกก่อน (เช่น ชื่อแบรนด์) เพื่อให้เลือกถูกตอนกรอกเอกสาร' };
  if ([...label].length > PROFILE_LABEL_MAX) return { ok: false, error: `ชื่อเรียกยาวเกิน ${PROFILE_LABEL_MAX} ตัวอักษร` };
  const d = isObj(raw.data) ? raw.data : {};
  const th = isObj(d.th) ? d.th : {}, en = isObj(d.en) ? d.en : {};
  const data: DocProfileData = {
    th: { name: str(th.name), address: str(th.address) },
    en: { name: str(en.name), address: str(en.address) },
    phone: str(d.phone), email: str(d.email), taxId: str(d.taxId), website: str(d.website)
  };
  if (!data.th.name && !data.en.name) return { ok: false, error: 'ใส่ชื่อบริษัท/แบรนด์อย่างน้อย 1 ภาษา' };
  const tooLong = (v: string, k: ProfileKey, where = '') => [...v].length > LIMIT[k] ? `${PROFILE_KEY_TH[k]}${where} ยาวเกิน ${LIMIT[k]} ตัวอักษร` : null;
  const why = tooLong(data.th.name, 'name', ' (ไทย)') ?? tooLong(data.en.name, 'name', ' (อังกฤษ)') ?? tooLong(data.th.address, 'address', ' (ไทย)')
    ?? tooLong(data.en.address, 'address', ' (อังกฤษ)') ?? tooLong(data.phone, 'phone') ?? tooLong(data.email, 'email') ?? tooLong(data.taxId, 'taxId')
    ?? tooLong(data.website, 'website');
  if (why) return { ok: false, error: why };
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) return { ok: false, error: `อีเมล "${data.email}" ไม่ถูกรูปแบบ (เช่น sale@example.com)` };
  if (data.taxId && !/^[0-9][0-9 -]*$/.test(data.taxId)) return { ok: false, error: 'เลขประจำตัวผู้เสียภาษีใส่ได้เฉพาะตัวเลข ขีด และเว้นวรรค' };
  for (const [k, v] of [['ชื่อ (ไทย)', data.th.name], ['ชื่อ (อังกฤษ)', data.en.name], ['โทรศัพท์', data.phone], ['อีเมล', data.email], ['เลขภาษี', data.taxId], ['เว็บไซต์', data.website]] as const) {
    if (v.includes('\n')) return { ok: false, error: `${k} ต้องเป็นบรรทัดเดียว` };
  }
  let logo: string | null = null;
  if (raw.logo !== undefined && raw.logo !== null && raw.logo !== '') {
    if (typeof raw.logo !== 'string' || !LOGO_URL.test(raw.logo)) return { ok: false, error: 'โลโก้ต้องเป็นรูปที่อัปโหลดในแอปนี้ (PNG/JPG/WebP) อัปโหลดใหม่อีกครั้ง' };
    logo = raw.logo;
  }
  return { ok: true, value: { label, data, logo } };
}

/** a profile's value for a key in a language ('' = the profile has none: the person types it) */
export function profileValue(d: DocProfileData, key: ProfileKey, lang: DocLang): string {
  if (key === 'name' || key === 'address') return d[lang][key] || '';
  return d[key];
}
